import React, { useEffect, useState } from 'react'
import Input from '../../components/elements/inputs/Input'
import { useTranslation } from 'next-i18next'
import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import Button from '../../components/elements/Button'
import { routes } from '../../utils/routes'
import { useForm, Controller } from 'react-hook-form'
import { Id, toast } from 'react-toastify'
import { signIn, useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import AuthLayout from '../../layouts/AuthLayout'
import { fetcher, isConstraintViolationList } from '../../api/io'
import { Api } from '../../../types/api'
import Context = Api.Context
import Checkbox from '../../components/elements/inputs/Checkbox'
import Head from 'next/head'
import { useOrganization } from '../../hooks/useOrganization'
import OnboardingImage from '../../components/elements/OnboardingImage'

type RegisterFormData = {
    firstName: string
    // lastName: string
    // company: string
    email: string
    password: string
    terms: string
}

export type IValidationError = {
    response: {
        data: {
            violations: { message: string; code: string }[]
        }
    }
}

export const getServerSideProps: GetServerSideProps<{ context: Context | null }> = async (
    ctx: GetServerSidePropsContext
) => {
    let context: Context | null = null

    try {
        context = await fetcher<null, Context>({
            url: '/context',
            hostname: 'backend.dann.local.sigbar.dev',
        })
    } catch (e) {
        console.log('error', e)
    }

    console.log('context', context)
    return {
        props: {
            ...(await serverSideTranslations(ctx.locale ? ctx.locale : '', ['common', 'register', 'terms', 'meta'])),
            context,
        },
    }
}

const Aanmelden = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { t } = useTranslation(['common', 'register', 'terms'])
    const router = useRouter()
    const authMethod = process.env.NEXT_PUBLIC_AUTH_METHOD || 'CREDENTIALS'
    const { organization } = useOrganization()
    const { redirect } = router.query
    const { status } = useSession()
    const {
        handleSubmit,
        control,
        setError,
        formState: { errors, isSubmitting },
        clearErrors,
    } = useForm<RegisterFormData>({
        mode: 'onTouched',
    })

    useEffect(() => {
        if (status === 'authenticated') {
            if (props.context?.organisation?.onboardingQuestionnaire !== null) {
                void router.replace(
                    routes.tests.path +
                        props.context?.organisation?.onboardingQuestionnaire.replace('/api/questionnaires', '') +
                        '?onboarding=true'
                )
            } else {
                void router.replace(typeof redirect === 'string' ? redirect : '/')
            }
        }
    }, [status])

    const [terms, setTerms] = useState<boolean>(false)

    const changeTerms = () => {
        if (!terms) {
            setTerms(true)
        } else {
            setTerms(false)
        }
    }

    const onSubmit = async (data: RegisterFormData) => {
        if (!terms) {
            setError('terms', {
                type: 'manual',
                message: t('terms-error', { ns: 'terms' }),
            })
            return
        }

        if (isSubmitting) return
        const id = toast.loading(<>{t('register-toast', { ns: 'register' })}</>, { toastId: 'register-toast' })

        try {
            const response = await fetcher<Api.RegisterRequest, Api.RegisterResponse | Api.ConstraintViolationList>({
                url: '/account/register',
                params: {
                    firstName: data.firstName,
                    middleName: '',
                    // lastName: data.lastName,
                    // company: data.company,
                    email: data.email,
                    password: data.password,
                    confirmPassword: data.password,
                    organisation: props.context?.organisation?.['@id'] ? props.context.organisation['@id'] : '',
                },
            })
            if (!response) {
                toast.update(id, {
                    render: <>{t('register-error', { ns: 'register' })}</>,
                    type: 'error',
                    isLoading: false,
                    autoClose: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    closeButton: true,
                    toastId: 'register-toast',
                })
            } else if (isConstraintViolationList(response)) {
                toast.update(id, {
                    render: <>{t(response.violations[0].message, { ns: 'register' })}</>,
                    type: 'error',
                    isLoading: false,
                    autoClose: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    closeButton: true,
                    toastId: 'register-toast',
                })
            } else {
                await handleSignIn(data, id)
            }
        } catch (e) {
            console.log('ERROR', e)
            // toast.update(id, {
            //     render: <>{t('register-error', { ns: 'register' })}</>,
            //     type: 'error',
            //     isLoading: false,
            //     autoClose: false,
            //     closeOnClick: true,
            //     pauseOnHover: true,
            //     draggable: true,
            //     closeButton: true,
            //     toastId: 'register-toast',
            // })
        }
    }

    const handleSignIn = async (data: RegisterFormData, toastId: Id) => {
        try {
            const res = await signIn('credentials', {
                email: data.email,
                password: data.password,
                callbackUrl: '/',
                redirect: false,
            })

            if (res) {
                if (res.status === 200) {
                    toast.update(toastId, {
                        render: <>{t('register-success', { ns: 'register' })}</>,
                        type: 'success',
                        isLoading: false,
                        autoClose: 3000,
                        closeOnClick: true,
                        closeButton: true,
                        toastId: 'register-toast',
                    })
                } else {
                    toast.update(toastId, {
                        render: <>{t('register-error', { ns: 'register' })}</>,
                        type: 'error',
                        isLoading: false,
                        autoClose: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        closeButton: true,
                        toastId: 'register-toast',
                    })
                }
            }
        } catch (e) {
            console.log('error', e)
        }
    }

    if (authMethod === 'OIDC') {
        return (
            <>
                <Head>
                    <title>{t('aanmelden.title', { ns: 'meta' })}</title>
                    <meta name="description" content={t('aanmelden.description', { ns: 'meta' })} />
                </Head>
                <AuthLayout>
                    <main className="min-h-screen lg:overflow-hidden">
                        <div className="flex min-h-screen">
                            <div className="relative hidden w-0 flex-1 lg:block min-h-full">
                                <OnboardingImage />
                            </div>
                            <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-24 xl:px-52  mt-[var(--dann-navbar-height-lg)]">
                                <div className="mx-auto w-full max-w-sm lg:w-96">
                                    <div>
                                        <h2>{t('OIDC-title', { ns: 'register' })}</h2>
                                        <p className="mt-4">{t('OIDC-description', { ns: 'register' })}</p>
                                        <p className="mt-4">
                                            {t('oidc-and-credentials-help', {
                                                organization: organization?.name,
                                                ns: 'register',
                                            })}
                                        </p>
                                        <Link href={routes.signIn.path}>
                                            <a href={routes.signIn.path}>
                                                {t('oidc-and-credentials-login', { ns: 'register' })}
                                            </a>
                                        </Link>
                                    </div>

                                    <div>
                                        <div className="pt-8">
                                            <p className=" text-xs leading-tight text-center text-gray-400 ">
                                                De Digitale Academie Noord-Nederland wordt mede mogelijk gemaakt door
                                                Nationaal Programma Groningen, Hanze, Rijksuniversiteit Groningen,
                                                Noorderpoort, NHL Stenden en Biblionet Groniningen.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </AuthLayout>
            </>
        )
    }

    return (
        <>
            <Head>
                <title>{t('aanmelden.title', { ns: 'meta' })}</title>
                <meta name="description" content={t('aanmelden.description', { ns: 'meta' })} />
            </Head>
            <AuthLayout>
                <main className="min-h-screen lg:overflow-hidden">
                    <div className="flex min-h-screen">
                        <div className="relative hidden w-0 flex-1 lg:block min-h-full">
                            <OnboardingImage />
                        </div>
                        <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-24 xl:px-52  mt-[var(--dann-navbar-height-lg)]">
                            <div className="mx-auto w-full max-w-sm lg:w-96">
                                <div>
                                    <h2>{t('ready-to-learn', { ns: 'register' })}</h2>
                                    <p className="mt-4">{t('description', { ns: 'register' })}</p>

                                    {authMethod === 'OIDC-AND-CREDENTIALS' && (
                                        <>
                                            <p className="mt-4">
                                                {t('oidc-and-credentials-help', {
                                                    organization: organization?.name,
                                                    ns: 'register',
                                                })}
                                            </p>
                                            <Link href={routes.signIn.path}>
                                                <a href={routes.signIn.path}>
                                                    {t('oidc-and-credentials-login', { ns: 'register' })}
                                                </a>
                                            </Link>
                                        </>
                                    )}
                                </div>

                                <div className="mt-8">
                                    <div>
                                        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate={true}>
                                            <div className="relative">
                                                <Controller
                                                    name="firstName"
                                                    rules={{ required: t('first-name-required', { ns: 'register' }) }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('first-name')}
                                                            type="text"
                                                            placeholder={t('first-name-placeholder')}
                                                            required
                                                        />
                                                    )}
                                                />

                                                {errors.firstName && (
                                                    <p
                                                        className="absolute text-xs -bottom-5 text-red-500 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.firstName?.message}
                                                    </p>
                                                )}
                                            </div>

                                            {/*<div className="relative">*/}
                                            {/*    <Controller*/}
                                            {/*        name="lastName"*/}
                                            {/*        rules={{ required: t('last-name-required', { ns: 'register' }) }}*/}
                                            {/*        control={control}*/}
                                            {/*        defaultValue={''}*/}
                                            {/*        render={({ field }) => (*/}
                                            {/*            <Input*/}
                                            {/*                {...field}*/}
                                            {/*                label={t('last-name')}*/}
                                            {/*                type="text"*/}
                                            {/*                placeholder={t('last-name-placeholder')}*/}
                                            {/*                required*/}
                                            {/*            />*/}
                                            {/*        )}*/}
                                            {/*    />*/}
                                            {/*    {errors.lastName && (*/}
                                            {/*        <p*/}
                                            {/*            className="absolute text-xs -bottom-5 text-red-500 inset-x-0 text-right "*/}
                                            {/*            role="alert"*/}
                                            {/*        >*/}
                                            {/*            {errors.lastName?.message}*/}
                                            {/*        </p>*/}
                                            {/*    )}*/}
                                            {/*</div>*/}

                                            {/*<div className="relative">*/}
                                            {/*    <Controller*/}
                                            {/*        name="company"*/}
                                            {/*        control={control}*/}
                                            {/*        defaultValue={''}*/}
                                            {/*        render={({ field }) => (*/}
                                            {/*            <Input*/}
                                            {/*                {...field}*/}
                                            {/*                label={t('working-at')}*/}
                                            {/*                type="text"*/}
                                            {/*                placeholder={t('company-placeholder')}*/}
                                            {/*            />*/}
                                            {/*        )}*/}
                                            {/*    />*/}
                                            {/*    <p*/}
                                            {/*        className="absolute text-xs -bottom-5 inset-x-0 text-right "*/}
                                            {/*        role="alert"*/}
                                            {/*    >*/}
                                            {/*        {t('optional')}*/}
                                            {/*    </p>*/}
                                            {/*</div>*/}

                                            <div className="relative">
                                                <Controller
                                                    name="email"
                                                    rules={{
                                                        required: t('email-required', { ns: 'register' }),
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: t('email-invalid', { ns: 'register' }),
                                                        },
                                                    }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('email')}
                                                            type="email"
                                                            placeholder={t('email-placeholder')}
                                                            required
                                                        />
                                                    )}
                                                />
                                                {errors.email && (
                                                    <p
                                                        className="absolute text-xs text-red-500 -bottom-5 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.email?.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="relative">
                                                <Controller
                                                    name="password"
                                                    rules={{
                                                        required: t('password-required', { ns: 'register' }),
                                                        pattern: {
                                                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d\w\W]{12,}$/,
                                                            message: t('password-invalid', { ns: 'register' }),
                                                        },
                                                    }}
                                                    control={control}
                                                    defaultValue={''}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            label={t('password')}
                                                            type="password"
                                                            placeholder={t('password-placeholder')}
                                                            required
                                                            subtext={t('password-requirements')}
                                                        />
                                                    )}
                                                />
                                                {errors.password && (
                                                    <p
                                                        className="absolute text-xs text-red-500 -bottom-5 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.password?.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="relative flex space-x-1 text-sm">
                                                <Checkbox
                                                    name="terms"
                                                    label=""
                                                    onChange={() => {
                                                        changeTerms()
                                                        clearErrors('terms')
                                                    }}
                                                />
                                                <p>
                                                    {t('terms-agree', { ns: 'terms' })}
                                                    <Link className="flex" href={routes.terms.path} target="_blank">
                                                        <a href={routes.terms.path} target="_blank">
                                                            {t('terms', { ns: 'terms' })}
                                                        </a>
                                                    </Link>
                                                </p>
                                                {errors.terms && (
                                                    <p
                                                        className="absolute text-xs text-red-500 -bottom-5 inset-x-0 text-right"
                                                        role="alert"
                                                    >
                                                        {errors.terms?.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <Button variant="primary" type="submit" className="w-full mt-4">
                                                    {t('register')}
                                                </Button>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div className="text-sm">
                                                    <Link href={routes.signIn.path}>
                                                        <a href={routes.signIn.path}>{t('already-account')}</a>
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className="pt-8">
                                                <p className=" text-xs leading-tight text-center text-gray-400 ">
                                                    De Digitale Academie Noord-Nederland wordt mede mogelijk gemaakt
                                                    door Nationaal Programma Groningen, Hanze, Rijksuniversiteit
                                                    Groningen, Noorderpoort, NHL Stenden en Biblionet Groniningen.
                                                </p>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </AuthLayout>
        </>
    )
}

export default Aanmelden
